

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaSync, FaSpinner } from 'react-icons/fa';
import alaram from "../../../../context/alaram.mp3";
import adminWebSocketService from '../../../../context/AdminWebSocketService';
import { getISTDateTime12hr } from '../../../../utils/dateTimeUtils'; // ✅ Import the utility
import { useNotification } from '../../../../context/Notifications/NotificationContext';
// ============================================
// STYLES
// ============================================
const styles = {
    container: {
        padding: '24px',
        // maxWidth: '1600px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'white',
        padding: '16px 24px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    },
    title: {
        margin: 0,
        color: '#e66d33',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    refreshButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        background: '#e66d33',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: '0.2s',
        fontSize: '14px'
    },
    tableContainer: {
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        overflowX: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px'
    },
    th: {
        background: '#f1f5f9',
        color: '#475569',
        fontWeight: '600',
        padding: '16px',
        textAlign: 'left',
        borderBottom: '2px solid #e2e8f0'
    },
    td: {
        padding: '16px',
        borderBottom: '1px solid #e2e8f0',
        color: '#334155'
    },
    statusBadge: {
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-block'
    },
    viewButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 14px',
        background: '#2563eb',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: '500',
        transition: '0.2s'
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
        color: '#94a3b8'
    },
    loadingOverlay: {
        textAlign: 'center',
        padding: '40px 20px',
        color: '#64748b'
    },
    notificationBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        background: '#dc2626',
        color: 'white',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        marginLeft: '8px',
        animation: 'pulse 1.5s ease-in-out infinite'
    },
    newOrderRow: {
        animation: 'highlightNew 2s ease-in-out'
    }
};

// ============================================
// CONSTANTS
// ============================================
const ALLOWED_STATUSES = [
    'HOLD',
    'PENDING',
    'CONFIRMED',
    'PROCESSING',
    'BEING_PREPARED',
    'ORDER_IS_READY',
    'WAITING_FOR_PICKUP',
    'ON_THE_WAY'
];

const API_URL = "https://backend.maamaas.com/food/api/orders/getall/users";

// ============================================
// COMPONENT
// ============================================
const FoodLiveOrders = () => {
    const navigate = useNavigate();
    const { addNotification } = useNotification(); // Add this

    // ─── State ───────────────────────────────────────────────────
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [newOrderCount, setNewOrderCount] = useState(0);
    const [hasNewOrders, setHasNewOrders] = useState(false);
    const [newOrderIds, setNewOrderIds] = useState(new Set());
    const [wsMessageCount, setWsMessageCount] = useState(0);
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
    const [orderIdSet, setOrderIdSet] = useState(new Set());
    // Add this near your other constants
    const ALLOWED_ORDER_TYPES = ['DELIVERY']; // Only show DELIVERY orders
    // ─── Audio ───────────────────────────────────────────────────
    const audioRef = useRef(null);
    const audioPlayedRef = useRef(false);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);

    // ─── Constants ──────────────────────────────────────────────
    const ALLOWED_STATUSES = [
        'HOLD',
        'PENDING',
        'CONFIRMED',
        'PROCESSING',
        'BEING_PREPARED',
        'ORDER_IS_READY',
        'WAITING_FOR_PICKUP',
        'ON_THE_WAY'
    ];

    const API_URL = "https://backend.maamaas.com/food/api/orders/getall/users";

    // ─── Audio Setup ──────────────────────────────────────────────
    useEffect(() => {
        audioRef.current = new Audio(alaram);
        audioRef.current.load();
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    // ─── Play Notification Sound ──────────────────────────────────
    const playNotificationSound = useCallback(async () => {
        if (!audioRef.current || isAudioPlaying) return;

        try {
            setIsAudioPlaying(true);
            audioRef.current.currentTime = 0;
            await audioRef.current.play();

            audioRef.current.onended = () => {
                setIsAudioPlaying(false);
                audioPlayedRef.current = false;
            };
        } catch (error) {
            console.warn('Audio playback failed:', error);
            setIsAudioPlaying(false);
            audioPlayedRef.current = false;
        }
    }, [isAudioPlaying]);

    // ─── Browser Notification ────────────────────────────────────
    const showBrowserNotification = useCallback((count) => {
        if (!("Notification" in window)) return;

        if (Notification.permission === "granted") {
            new Notification(`🆕 ${count} New Food Order${count > 1 ? 's' : ''}!`, {
                body: `Click to view ${count} new food order${count > 1 ? 's' : ''}.`,
                icon: "🍽️",
                silent: true
            });
        }
    }, []);

    // ─── Request Notification Permission ─────────────────────────
    useEffect(() => {
        if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission();
        }
    }, []);

    // ─── Filter Orders by Status ──────────────────────────────────
    const filterOrdersByStatus = useCallback((orderArray) => {
        if (!Array.isArray(orderArray)) return [];
        return orderArray.filter(order => {
            const status = order.status?.toUpperCase() || '';
            const orderType = order.orderType?.toUpperCase() || '';

            // ✅ Check BOTH status AND orderType
            return ALLOWED_STATUSES.includes(status) && ALLOWED_ORDER_TYPES.includes(orderType);
        });
    }, []);
    // ─── Process WebSocket Order ──────────────────────────────
    // const handleWebSocketOrder = useCallback((orderData) => {
    //     //console.log('🆕 New order from WebSocket:', orderData);

    //     const orderId = orderData.id || orderData.orderId;

    //     if (orderIdSet.has(orderId)) {
    //         //console.log(`⚠️ Order ${orderId} already exists, skipping`);
    //         return;
    //     }

    //     const status = orderData.status?.toUpperCase() || '';
    //     if (!ALLOWED_STATUSES.includes(status)) {
    //         //console.log(`⏭️ Order ${orderId} status "${status}" not allowed, skipping`);
    //         return;
    //     }

    //     //console.log(`✅ Adding NEW order ${orderId} from WebSocket`);

    //     setOrderIdSet(prev => new Set(prev).add(orderId));
    //     setOrders(prev => [orderData, ...prev]);
    //     setNewOrderIds(prev => new Set(prev).add(orderId));
    //     setNewOrderCount(prev => prev + 1);
    //     setHasNewOrders(true);

    //     // 🔔 ADD GLOBAL NOTIFICATION
    //     addNotification('Live orders', 1);

    //     setTimeout(() => {
    //         setNewOrderIds(prev => {
    //             const newSet = new Set(prev);
    //             newSet.delete(orderId);
    //             return newSet;
    //         });
    //     }, 5000);
    // }, [orderIdSet, addNotification]); // ✅ Only depends on orderIdSet and addNotification
    const handleWebSocketOrder = useCallback((orderData) => {
        //console.log('🆕 New order from WebSocket:', orderData);

        const orderId = orderData.id || orderData.orderId;
        const status = orderData.status?.toUpperCase() || '';
        const orderType = orderData.orderType?.toUpperCase() || '';

        // ✅ Check if order already exists
        if (orderIdSet.has(orderId)) {
            //console.log(`⚠️ Order ${orderId} already exists, skipping`);
            return;
        }

        // ✅ Check BOTH status AND orderType
        if (!ALLOWED_STATUSES.includes(status)) {
            //console.log(`⏭️ Order ${orderId} status "${status}" not allowed, skipping`);
            return;
        }

        if (!ALLOWED_ORDER_TYPES.includes(orderType)) {
            //console.log(`⏭️ Order ${orderId} orderType "${orderType}" not DELIVERY, skipping`);
            return;
        }

        //console.log(`✅ Adding NEW DELIVERY order ${orderId} from WebSocket`);

        setOrderIdSet(prev => new Set(prev).add(orderId));
        setOrders(prev => [orderData, ...prev]);
        setNewOrderIds(prev => new Set(prev).add(orderId));
        setNewOrderCount(prev => prev + 1);
        setHasNewOrders(true);

        // 🔔 Only add notification for DELIVERY orders
        addNotification('Live orders', 1);

        setTimeout(() => {
            setNewOrderIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(orderId);
                return newSet;
            });
        }, 5000);
    }, [orderIdSet, addNotification]);
    // ─── Fetch Initial Orders from API ──────────────────────────
    const fetchInitialOrders = useCallback(async () => {
        //console.log('🔄 Fetching initial food orders from API...');
        //console.log(`📍 API URL: ${API_URL}`);

        try {
            setError(null);
            const token = localStorage.getItem("authToken");

            if (token) {
                //console.log(`🔑 Token preview: ${token.substring(0, 20)}...`);
            } else {
                console.warn('⚠️ No auth token found in localStorage!');
            }

            //console.log('📤 Making API request...');
            const response = await fetch(API_URL, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            //console.log(`📥 Response status: ${response.status}`);

            if (!response.ok) {
                let errorMessage = `Failed to fetch orders: ${response.status}`;
                try {
                    const errorData = await response.json();
                    console.error('❌ Error response body:', errorData);
                    errorMessage = errorData.message || errorData.error || errorMessage;
                } catch (e) {
                    console.warn('⚠️ Could not parse error response as JSON');
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            //console.log(`✅ API fetch complete: ${data.length} orders total`);

            const filteredOrders = filterOrdersByStatus(data);
            //console.log(`✅ Filtered to ${filteredOrders.length} orders with allowed statuses`);

            const reversedOrders = [...filteredOrders].reverse();

            const ids = new Set(reversedOrders.map(o => o.orderId));
            setOrderIdSet(ids);

            setOrders(reversedOrders);
            setIsInitialLoadComplete(true);
            //console.log(`📊 Loaded ${reversedOrders.length} orders`);

        } catch (err) {
            console.error("❌ Error fetching initial orders:", err);
            setError(err.message || "Could not load food orders.");
            setOrders([]);
            setIsInitialLoadComplete(true);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [filterOrdersByStatus]); // ✅ Only depends on filterOrdersByStatus

    // ─── Handle WebSocket Messages ──────────────────────────────
    const handleWebSocketMessage = useCallback((event) => {
        //console.log('📨 WebSocket message received');
        setWsMessageCount(prev => prev + 1);

        const { topic, data } = event.detail;
        //console.log(`📋 Topic: ${topic}`);

        if (topic !== 'admin-all-orders') {
            //console.log(`ℹ️ Ignoring topic: ${topic}`);
            return;
        }

        //console.log('✅ Processing admin-all-orders');

        if (Array.isArray(data)) {
            //console.log(`📦 Received ${data.length} orders from WebSocket`);
            data.forEach(order => handleWebSocketOrder(order));
        } else if (data && typeof data === 'object') {
            //console.log('📦 Received single order from WebSocket');
            handleWebSocketOrder(data);
        } else {
            console.warn('⚠️ Unknown data format:', data);
        }
    }, [handleWebSocketOrder]); // ✅ Only depends on handleWebSocketOrder

    // ─── WebSocket Setup ──────────────────────────────────────────
    useEffect(() => {
        //console.log('🍽️ FoodLiveOrders mounted');

        const wsStatus = adminWebSocketService.isConnected();
        //console.log(`🔗 WebSocket: ${wsStatus ? '✅ Connected' : '❌ Not connected'}`);

        fetchInitialOrders();

        window.addEventListener('admin-websocket-message', handleWebSocketMessage);

        return () => {
            //console.log('🧹 Cleaning up FoodLiveOrders');
            window.removeEventListener('admin-websocket-message', handleWebSocketMessage);
        };
    }, []); // ✅✅✅ EMPTY DEPENDENCY ARRAY - RUNS ONCE ON MOUNT

    // ─── Manual Refresh ──────────────────────────────────────────
    const handleRefresh = useCallback(() => {
        //console.log('🔄 Manual refresh triggered');
        setRefreshing(true);
        fetchInitialOrders();
    }, [fetchInitialOrders]);

    // ─── Clear Notification ──────────────────────────────────────
    const handleClearNotification = useCallback(() => {
        //console.log('🔔 Clearing notifications');
        setNewOrderCount(0);
        setHasNewOrders(false);
        setNewOrderIds(new Set());
    }, []);

    // ─── Helper Functions ─────────────────────────────────────────
    const getStatusColor = useCallback((status) => {
        if (!status) return '#e2e8f0';
        const s = status.toUpperCase();
        const colors = {
            'DELIVERED': '#48bb78',
            'COMPLETED': '#48bb78',
            'CANCELLED': '#fc8181',
            'HOLD': '#ed8936',
            'PENDING': '#f6e05e',
            'CONFIRMED': '#4299e1',
            'PROCESSING': '#9f7aea',
            'BEING_PREPARED': '#9f7aea',
            'ORDER_IS_READY': '#68d391',
            'WAITING_FOR_PICKUP': '#4fd1c5',
            'ON_THE_WAY': '#63b3ed'
        };
        return colors[s] || '#e2e8f0';
    }, []);

    const formatDateTimeIST = useCallback((order) => {
        if (order.orderDateAndTime) {
            return getISTDateTime12hr(order.orderDateAndTime);
        }
        if (order.date && order.time) {
            const dateTimeStr = `${order.date}T${order.time}`;
            return getISTDateTime12hr(dateTimeStr);
        }
        return '-';
    }, []);

    const isNewOrder = useCallback((orderId) => {
        return newOrderIds.has(orderId);
    }, [newOrderIds]);


    // ─── Render ───────────────────────────────────────────────────
    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <h2 style={styles.title}>
                    Live Food Orders
                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 'normal' }}>
                        ({orders.length} active)
                    </span>
                    {hasNewOrders && newOrderCount > 0 && (
                        <span style={styles.notificationBadge}>
                            🔊 {newOrderCount} new
                        </span>
                    )}
                </h2>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {hasNewOrders && (
                        <button
                            onClick={handleClearNotification}
                            style={{
                                padding: '6px 12px',
                                background: 'transparent',
                                color: '#64748b',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                        >
                            Clear Alert
                        </button>
                    )}
                    <button
                        onClick={handleRefresh}
                        style={styles.refreshButton}
                        disabled={loading || refreshing}
                    >
                        {refreshing ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaSync />}
                        {refreshing ? 'Refreshing...' : 'Refresh Orders'}
                    </button>
                </div>
            </div>

            {/* Table Area */}
            <div style={styles.tableContainer}>
                {loading ? (
                    <div style={styles.loadingOverlay}>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={28} color="#e66d33" />
                        <p style={{ marginTop: '12px', fontWeight: '500' }}>Loading food orders...</p>
                    </div>
                ) : error ? (
                    <div style={styles.emptyState}>
                        <p style={{ color: '#dc2626', fontWeight: '600' }}>⚠️ {error}</p>
                        <button
                            onClick={handleRefresh}
                            style={{ ...styles.refreshButton, margin: '12px auto' }}
                        >
                            Try Again
                        </button>
                    </div>
                ) : orders.length === 0 ? (
                    <div style={styles.emptyState}>
                        <h3 style={{ color: '#475569', margin: '0 0 8px 0' }}>No Food Orders</h3>
                        <p style={{ color: '#94a3b8', margin: '0' }}>
                            There are currently no active food orders.
                            <br />New orders will appear here automatically.
                        </p>
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Order ID</th>
                                <th style={styles.th}>Date & Time</th>
                                <th style={styles.th}>Customer</th>
                                <th style={styles.th}>Phone</th>
                                <th style={styles.th}>Restaurant</th>
                                <th style={styles.th}>Total</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => {
                                const orderId = order.orderId;
                                const isNew = isNewOrder(orderId);

                                return (
                                    <tr
                                        key={orderId}
                                        style={{
                                            ...(isNew ? styles.newOrderRow : {}),
                                            background: isNew ? '#fef3e8' : 'transparent',
                                            transition: 'background 0.3s ease'
                                        }}
                                    >
                                        <td style={styles.td}>
                                            <strong>#{order.orderId}</strong>
                                            {isNew && (
                                                <span style={{
                                                    marginLeft: '8px',
                                                    padding: '2px 8px',
                                                    background: '#dc2626',
                                                    color: 'white',
                                                    borderRadius: '12px',
                                                    fontSize: '10px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    NEW
                                                </span>
                                            )}
                                        </td>
                                        {/* ✅ FIXED: Show date and time in IST */}
                                        <td style={styles.td}>
                                            {formatDateTimeIST(order)}
                                        </td>
                                        <td style={styles.td}>
                                            <div>{order.userName || 'Unknown'}</div>
                                        </td>
                                        {/* ✅ FIXED: Show phone number using mobileNo */}
                                        <td style={styles.td}>
                                            {order.phoneNumber || order.mobileNo || '-'}
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: '500' }}>
                                                {order.vendorRegisteredName || 'Unknown'}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                {order.deliveryDistanceKm ? `${parseFloat(order.deliveryDistanceKm).toFixed(2)} km away` : '-'}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{ fontWeight: '600', color: '#16a34a' }}>
                                                ₹{order.totalAmount ? order.totalAmount.toFixed(2) : order.grandTotal ? order.grandTotal.toFixed(2) : '0.00'}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                background: getStatusColor(order.status),
                                                color: order.status?.toUpperCase() === 'HOLD' || order.status?.toUpperCase() === 'PENDING' ? '#92400e' : '#fff'
                                            }}>
                                                {order.status || 'PENDING'}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <button
                                                style={styles.viewButton}
                                                onClick={() => navigate(`/admin/orders/details/${order.orderId}`)}
                                            >
                                                <FaEye /> View
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* WebSocket Status */}
            {/* <div style={{
                marginTop: '16px',
                padding: '12px 16px',
                background: adminWebSocketService.isConnected() ? '#dcfce7' : '#fee2e2',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: adminWebSocketService.isConnected() ? '#166534' : '#991b1b'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: adminWebSocketService.isConnected() ? '#22c55e' : '#ef4444',
                        animation: adminWebSocketService.isConnected() ? 'pulse 1.5s ease-in-out infinite' : 'none'
                    }}></span>
                    {adminWebSocketService.isConnected() 
                        ? '✅ WebSocket connected - Real-time updates active' 
                        : '❌ WebSocket disconnected - Please refresh'}
                </div>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Messages received: {wsMessageCount}
                </span>
            </div> */}

            {/* Inline Keyframes */}
            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0% { opacity: 1; }
                    50% { opacity: 0.5; }
                    100% { opacity: 1; }
                }
                @keyframes highlightNew {
                    0% { background: #fef3e8; }
                    50% { background: #ffe4d6; }
                    100% { background: #fef3e8; }
                }
            `}</style>
        </div>
    );
};

export default FoodLiveOrders;